// iter127 — `/explore` data-interrogation surface.
//
// Single search-first surface available to ALL authenticated roles. Backend
// double-filters every result by tenant (contextvar) + role-based visibility
// (filter_visible_risks / filter_visible_signals / can_see_kri), so a Viewer
// in the Cyber domain only sees Cyber-domain risks at their clearance band.
//
// Four pivot dimensions: by Risk, by Theme, by Supplier, by Owner.
// Plus a single risk-impact graph at `/explore/risks/:id/impact`.
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search, FileText, Building2, ShieldCheck, BarChart3,
  Users, Radio, Layers, AlertTriangle, X, Star, Printer, Download, Trash2, Share2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DIMENSIONS = [
  { id: "search",    label: "Search",    icon: Search,    hint: "Free-text across risks, suppliers, signals, controls, KRIs and briefings." },
  { id: "risks",     label: "By Risk",   icon: AlertTriangle, hint: "The full register, filterable by theme, owner, status and residual band." },
  { id: "themes",    label: "By Theme",  icon: Layers,    hint: "9 risk theme tiles with counts, max residual and top three risks." },
  { id: "suppliers", label: "By Supplier", icon: Building2, hint: "Supplier directory filterable by tier, country, sanctioned, SPOF." },
  { id: "owners",    label: "By Owner",  icon: Users,     hint: "Each owner's portfolio: how many risks, residual total, themes covered." },
];

const ROLE_PILLS = {
  admin: "bg-purple-100 text-purple-800",
  org_admin: "bg-purple-100 text-purple-800",
  executive: "bg-indigo-100 text-indigo-800",
  cro: "bg-indigo-100 text-indigo-800",
  manager: "bg-blue-100 text-blue-800",
  contributor: "bg-emerald-100 text-emerald-800",
  viewer: "bg-slate-100 text-slate-800",
};

export default function Explore() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const dim = params.get("d") || "search";
  const setDim = (d, extra = {}) => setParams({ d, ...extra });
  const [savedViews, setSavedViews] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);

  // Current dimension filter state — lifted so the SaveButton can grab it.
  const [currentState, setCurrentState] = useState({ filters: {}, q: "" });

  useEffect(() => {
    api.get("/explore/saved-views").then((res) => setSavedViews(res.data.items || []));
  }, [refreshTick]);

  const applySavedView = (v) => {
    const next = { d: v.dimension };
    Object.entries(v.filters || {}).forEach(([k, val]) => { if (val) next[k] = val; });
    if (v.q) next.q = v.q;
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="explore-page">
      <div className="mx-auto max-w-7xl px-6 py-8" id="explore-printable">
        <header className="mb-8 print:mb-4">
          <p className="text-xs font-semibold uppercase tracking-[2px] text-ek-ink/50">Explore</p>
          <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Interrogate your data.</h1>
          <p className="mt-3 max-w-2xl text-base text-ek-ink/65 print:hidden">
            Search and pivot the register, supplier graph, signal feed and ownership map.
            Signed in as <span className="font-semibold text-ek-ink">{user?.role?.toUpperCase()}</span> —
            you see {user?.role === "admin" || user?.role === "org_admin" || user?.role === "cro" || user?.role === "executive"
              ? "the whole tenant within your clearance." : "only what's in your domain scope and clearance band."}
          </p>
        </header>

        {/* Toolbar — print + CSV + save view */}
        <div className="flex flex-wrap items-center gap-2 mb-5 print:hidden" data-testid="explore-toolbar">
          <SaveViewButton dim={dim} state={currentState} onSaved={() => setRefreshTick((t) => t + 1)} />
          {(dim === "risks" || dim === "suppliers" || dim === "owners") && (
            <ExportCsvButton dim={dim} state={currentState} />
          )}
          <button
            onClick={() => window.print()}
            data-testid="explore-print-btn"
            className="ek-btn-outline flex items-center gap-1.5 !py-2 text-sm"
            title="Print or save as PDF using the browser print dialog"
          >
            <Printer size={14} /> Print
          </button>
        </div>

        <div className="grid grid-cols-12 gap-8">
          {/* Left-rail dimension switcher */}
          <nav className="col-span-12 md:col-span-3 print:hidden" aria-label="Pivot dimensions" data-testid="explore-dimensions">
            <ul className="space-y-1">
              {DIMENSIONS.map((d) => {
                const Icon = d.icon;
                const active = dim === d.id;
                return (
                  <li key={d.id}>
                    <button
                      onClick={() => setDim(d.id)}
                      data-testid={`explore-dim-${d.id}`}
                      className={`w-full flex items-start gap-3 rounded px-3 py-3 text-left transition ${
                        active ? "bg-ek-purple-fog text-ek-purple" : "hover:bg-slate-50 text-ek-ink"
                      }`}
                    >
                      <Icon size={18} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-semibold">{d.label}</div>
                        <div className="text-xs text-ek-ink/55 mt-0.5">{d.hint}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            <SavedViewsList
              views={savedViews}
              onApply={applySavedView}
              onChange={() => setRefreshTick((t) => t + 1)}
            />
            <div className="mt-6 rounded border border-ek-ink/10 p-3 text-xs text-ek-ink/65 leading-relaxed">
              <p className="font-semibold text-ek-ink mb-1">Your visibility</p>
              <p>
                Tenant: <span className="font-mono text-[11px]">{user?.email?.split("@")[1]}</span><br/>
                Clearance: <span className="font-mono text-[11px]">{user?.clearance}/5</span><br/>
                Domains: {Array.isArray(user?.domains) ? user.domains.join(", ") : "all"}
              </p>
            </div>
          </nav>

          {/* Right pane */}
          <section className="col-span-12 md:col-span-9 print:col-span-12" data-testid="explore-content">
            {dim === "search"    && <SearchView onState={setCurrentState} />}
            {dim === "risks"     && <RisksView onState={setCurrentState} />}
            {dim === "themes"    && <ThemesView onPickTheme={(t) => { setParams({ d: "risks", theme: t }); }} onState={setCurrentState} />}
            {dim === "suppliers" && <SuppliersView onState={setCurrentState} />}
            {dim === "owners"    && <OwnersView onState={setCurrentState} />}
          </section>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// 1) Federated search
// -----------------------------------------------------------------------------

function SearchView({ onState }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { onState && onState({ filters: {}, q }); }, [q, onState]);

  useEffect(() => {
    if (!q || q.length < 2) { setData(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/explore/search?q=${encodeURIComponent(q)}&limit=5`);
        setData(res.data);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <div className="flex items-center gap-2 rounded border border-ek-ink/15 bg-white px-4 py-3 focus-within:border-ek-purple">
        <Search size={18} className="text-ek-ink/50 flex-shrink-0" />
        <input
          type="text" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search risks, suppliers, signals, controls, KRIs and briefings…"
          data-testid="explore-search-input"
          className="w-full outline-none text-base text-ek-ink placeholder:text-ek-ink/40"
        />
        {q && (
          <button onClick={() => setQ("")} className="text-ek-ink/40 hover:text-ek-ink" aria-label="Clear">
            <X size={18} />
          </button>
        )}
      </div>
      {loading && <p className="mt-6 text-sm text-ek-ink/50">Searching…</p>}
      {!loading && q.length >= 2 && data && data.total === 0 && (
        <p className="mt-6 text-sm text-ek-ink/50">No matches for &quot;{q}&quot; within your visibility.</p>
      )}
      {!loading && data && data.total > 0 && (
        <div className="mt-6 space-y-6" data-testid="explore-search-results">
          {Object.entries(data.results).map(([kind, items]) => items.length > 0 && (
            <SearchGroup key={kind} kind={kind} items={items} />
          ))}
        </div>
      )}
      {!q && (
        <p className="mt-6 text-sm text-ek-ink/55">
          Type at least 2 characters. Search is case-insensitive and runs across every entity
          you&apos;re entitled to see. Try &quot;cyber&quot;, &quot;sanctions&quot;, &quot;tier 1&quot;, or a supplier name.
        </p>
      )}
    </div>
  );
}

function SearchGroup({ kind, items }) {
  const meta = {
    risks:     { Icon: AlertTriangle, label: "Risks" },
    suppliers: { Icon: Building2,     label: "Suppliers" },
    controls:  { Icon: ShieldCheck,   label: "Controls" },
    kris:      { Icon: BarChart3,     label: "KRIs" },
    briefings: { Icon: FileText,      label: "Briefings" },
    signals:   { Icon: Radio,         label: "Signals" },
  }[kind] || { Icon: FileText, label: kind };
  const Icon = meta.Icon;
  return (
    <div data-testid={`explore-search-group-${kind}`}>
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[1.4px] text-ek-ink/60">
        <Icon size={14} /> {meta.label} <span className="text-ek-ink/40">({items.length})</span>
      </h3>
      <ul className="mt-3 divide-y divide-ek-ink/10 rounded border border-ek-ink/10">
        {items.map((it) => (
          <li key={it.id} className="px-4 py-3 hover:bg-slate-50">
            <SearchResultRow kind={kind} item={it} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function SearchResultRow({ kind, item }) {
  if (kind === "risks") {
    return (
      <Link to={`/explore/risks/${item.id}/impact`} className="block" data-testid={`explore-search-risk-${item.id}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ek-ink truncate">{item.title}</p>
            <p className="text-xs text-ek-ink/55 mt-1">
              {item.theme_id} · residual {item.residual_score}/25 · {item.status}
            </p>
          </div>
          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
            (item.residual_score || 0) >= 16 ? "bg-rose-100 text-rose-800" :
            (item.residual_score || 0) >= 12 ? "bg-amber-100 text-amber-800" :
            "bg-emerald-100 text-emerald-800"
          }`}>{(item.residual_score || 0) >= 16 ? "Critical" : (item.residual_score || 0) >= 12 ? "High" : "Stable"}</span>
        </div>
      </Link>
    );
  }
  if (kind === "suppliers") {
    return <div><p className="text-sm font-semibold text-ek-ink">{item.legal_name}</p><p className="text-xs text-ek-ink/55 mt-1">Tier {item.tier} · {item.country}</p></div>;
  }
  if (kind === "controls") {
    return <div><p className="text-sm font-semibold text-ek-ink">{item.name}</p><p className="text-xs text-ek-ink/55 mt-1">{item.control_id} · {item.framework_ref}</p></div>;
  }
  if (kind === "kris") {
    return <div><p className="text-sm font-semibold text-ek-ink">{item.name}</p><p className="text-xs text-ek-ink/55 mt-1">{item.category} · current {item.current_value}</p></div>;
  }
  if (kind === "briefings") {
    return <Link to={`/explore/risks/${item.id}/impact`} className="block"><p className="text-sm font-semibold text-ek-ink">{(item.summary || "").slice(0, 80)}…</p><p className="text-xs text-ek-ink/55 mt-1">Risk {item.id}</p></Link>;
  }
  if (kind === "signals") {
    return <div><p className="text-sm font-semibold text-ek-ink">{item.summary}</p><p className="text-xs text-ek-ink/55 mt-1">{item.severity} · {item.source} · {item.received_at?.slice(0, 10)}</p></div>;
  }
  return null;
}

// -----------------------------------------------------------------------------
// 2) Risks register pivot
// -----------------------------------------------------------------------------

function RisksView({ onState }) {
  const [params] = useSearchParams();
  const initialTheme = params.get("theme") || "";
  const [theme, setTheme] = useState(initialTheme);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => { onState && onState({ filters: { theme, status }, q }); }, [theme, status, q, onState]);

  useEffect(() => {
    const load = async () => {
      const qs = new URLSearchParams();
      if (theme) qs.set("theme", theme);
      if (status) qs.set("status", status);
      if (q && q.length >= 2) qs.set("q", q);
      const res = await api.get(`/explore/risks?${qs.toString()}`);
      setRows(res.data.items || []);
    };
    load();
  }, [theme, status, q]);

  return (
    <div data-testid="explore-risks-view">
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <FilterInput label="Theme" value={theme} onChange={setTheme} placeholder="t_cyber" testid="filter-theme" />
        <FilterInput label="Status" value={status} onChange={setStatus} placeholder="open / mitigating / closed" testid="filter-status" />
        <FilterInput label="Search" value={q} onChange={setQ} placeholder="title contains…" testid="filter-q" />
        <div className="text-xs text-ek-ink/55 ml-auto">{rows.length} risks visible</div>
      </div>
      <ul className="divide-y divide-ek-ink/10 rounded border border-ek-ink/10" data-testid="explore-risks-list">
        {rows.map((r) => (
          <li key={r.risk_id}>
            <Link to={`/explore/risks/${r.risk_id}/impact`} className="flex items-start justify-between gap-4 px-4 py-3 hover:bg-slate-50" data-testid={`risk-row-${r.risk_id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ek-ink truncate">{r.title}</p>
                <p className="text-xs text-ek-ink/55 mt-1">
                  {r.theme_id} · residual {r.residual_score}/25 · {r.status}
                </p>
              </div>
              <ResidualBadge score={r.residual_score} />
            </Link>
          </li>
        ))}
        {rows.length === 0 && <li className="px-4 py-6 text-sm text-ek-ink/50">No risks match your filters within your visibility.</li>}
      </ul>
    </div>
  );
}

function ResidualBadge({ score }) {
  const s = score || 0;
  const cls = s >= 16 ? "bg-rose-100 text-rose-800" : s >= 12 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800";
  return <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${cls}`}>{s}/25</span>;
}

function FilterInput({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="flex flex-col text-xs">
      <span className="text-ek-ink/55 mb-1 uppercase tracking-wider font-medium">{label}</span>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testid}
        className="rounded border border-ek-ink/15 px-3 py-2 text-sm outline-none focus:border-ek-purple" />
    </label>
  );
}

// -----------------------------------------------------------------------------
// 3) Theme tiles
// -----------------------------------------------------------------------------

function ThemesView({ onPickTheme, onState }) {
  const [themes, setThemes] = useState([]);
  useEffect(() => { onState && onState({ filters: {}, q: "" }); }, [onState]);
  useEffect(() => { api.get("/explore/themes").then((res) => setThemes(res.data.themes || [])); }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="explore-themes-grid">
      {themes.map((t) => (
        <button
          key={t.theme_id}
          onClick={() => onPickTheme(t.theme_id)}
          data-testid={`theme-tile-${t.theme_id}`}
          className="text-left rounded border border-ek-ink/10 p-5 hover:border-ek-purple hover:shadow-sm transition"
        >
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-base font-semibold text-ek-ink">{t.name}</h3>
            <ResidualBadge score={t.max_residual} />
          </div>
          <p className="text-xs text-ek-ink/55 mt-1">
            {t.count} risks · {t.open_count} open · max residual {t.max_residual}/25
          </p>
          <ul className="mt-3 space-y-1">
            {(t.top3 || []).map((r) => (
              <li key={r.risk_id} className="text-xs text-ek-ink/75 truncate">
                · {r.title} <span className="text-ek-ink/40">({r.residual_score}/25)</span>
              </li>
            ))}
          </ul>
        </button>
      ))}
      {themes.length === 0 && <p className="text-sm text-ek-ink/50">No themes within your domain scope.</p>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// 4) Suppliers
// -----------------------------------------------------------------------------

function SuppliersView({ onState }) {
  const [tier, setTier] = useState("");
  const [country, setCountry] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => { onState && onState({ filters: { tier, country }, q }); }, [tier, country, q, onState]);
  useEffect(() => {
    const qs = new URLSearchParams();
    if (tier) qs.set("tier", tier);
    if (country) qs.set("country", country);
    if (q.length >= 2) qs.set("q", q);
    api.get(`/explore/suppliers?${qs.toString()}`).then((res) => setRows(res.data.items || []));
  }, [tier, country, q]);
  return (
    <div data-testid="explore-suppliers-view">
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <FilterInput label="Tier" value={tier} onChange={setTier} placeholder="1" testid="filter-supplier-tier" />
        <FilterInput label="Country" value={country} onChange={setCountry} placeholder="GB" testid="filter-supplier-country" />
        <FilterInput label="Search" value={q} onChange={setQ} placeholder="legal name…" testid="filter-supplier-q" />
        <div className="text-xs text-ek-ink/55 ml-auto">{rows.length} suppliers</div>
      </div>
      <ul className="divide-y divide-ek-ink/10 rounded border border-ek-ink/10" data-testid="explore-suppliers-list">
        {rows.map((s) => (
          <li key={s.supplier_id} className="px-4 py-3 hover:bg-slate-50">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ek-ink truncate">{s.legal_name}</p>
                <p className="text-xs text-ek-ink/55 mt-1">Tier {s.tier} · {s.country} · {s.supplier_id}</p>
              </div>
              {s.sanctioned && <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-rose-100 text-rose-800">Sanctioned</span>}
            </div>
          </li>
        ))}
        {rows.length === 0 && <li className="px-4 py-6 text-sm text-ek-ink/50">No suppliers match.</li>}
      </ul>
    </div>
  );
}

// -----------------------------------------------------------------------------
// 5) Owners
// -----------------------------------------------------------------------------

function OwnersView({ onState }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { onState && onState({ filters: {}, q: "" }); }, [onState]);
  useEffect(() => { api.get("/explore/owners").then((res) => setRows(res.data.items || [])); }, []);
  return (
    <div data-testid="explore-owners-view">
      <ul className="divide-y divide-ek-ink/10 rounded border border-ek-ink/10">
        {rows.map((o) => (
          <li key={o.owner_user_id} className="px-4 py-3 hover:bg-slate-50">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ek-ink truncate">{o.name}</p>
                <p className="text-xs text-ek-ink/55 mt-1">{o.email} · {o.themes.length} themes</p>
              </div>
              <div className="text-right">
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${ROLE_PILLS[o.role] || "bg-slate-100 text-slate-700"}`}>{o.role}</span>
                <p className="text-xs text-ek-ink/55 mt-1">{o.risk_count} risks · residual {o.residual_total}</p>
              </div>
            </div>
          </li>
        ))}
        {rows.length === 0 && <li className="px-4 py-6 text-sm text-ek-ink/50">No owners within your visibility.</li>}
      </ul>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Toolbar: Save view, CSV export, Saved views list (iter128)
// -----------------------------------------------------------------------------

function SaveViewButton({ dim, state, onSaved }) {
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const submit = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/explore/saved-views", {
        name: name.trim(),
        dimension: dim,
        filters: state?.filters || {},
        q: state?.q || null,
        pinned: false,
      });
      setName(""); setOpen(false);
      onSaved && onSaved();
      toast.success("View saved");
    } catch {
      toast.error("Could not save view");
    }
  };
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="explore-save-view-btn"
        className="ek-btn-outline flex items-center gap-1.5 !py-2 text-sm"
        title="Save the current filters as a personal view"
      >
        <Star size={14} /> Save view
      </button>
      {open && (
        <div className="absolute z-10 mt-2 w-64 rounded border border-ek-ink/15 bg-white p-3 shadow-md" data-testid="save-view-popover">
          <p className="text-xs text-ek-ink/60 mb-2">Save as a personal view (only you can see / use it).</p>
          <input
            autoFocus value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. My critical cyber risks"
            data-testid="save-view-name-input"
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            className="w-full rounded border border-ek-ink/15 px-2 py-1.5 text-sm outline-none focus:border-ek-purple"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button onClick={() => { setOpen(false); setName(""); }} className="text-xs text-ek-ink/60 hover:text-ek-ink">Cancel</button>
            <button onClick={submit} data-testid="save-view-submit-btn" className="text-xs font-semibold text-ek-purple hover:underline">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ExportCsvButton({ dim, state }) {
  const qs = useMemo(() => {
    const p = new URLSearchParams({ dimension: dim });
    Object.entries(state?.filters || {}).forEach(([k, v]) => { if (v) p.set(k, v); });
    if (state?.q && state.q.length >= 2) p.set("q", state.q);
    return p.toString();
  }, [dim, state]);
  const onClick = async () => {
    try {
      const res = await api.get(`/explore/export.csv?${qs}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `erio-${dim}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("CSV downloaded");
    } catch {
      toast.error("CSV export failed");
    }
  };
  return (
    <button
      onClick={onClick}
      data-testid="explore-export-csv-btn"
      className="ek-btn-outline flex items-center gap-1.5 !py-2 text-sm"
      title="Download the current view as a CSV (respects all your filters and visibility)"
    >
      <Download size={14} /> Export CSV
    </button>
  );
}

function SavedViewsList({ views, onApply, onChange }) {
  const own = (views || []).filter((v) => v.is_mine);
  const shared = (views || []).filter((v) => !v.is_mine);

  const toggleShare = async (v) => {
    try {
      await api.patch(`/explore/saved-views/${v.view_id}`, {
        name: v.name, dimension: v.dimension, filters: v.filters || {},
        q: v.q, pinned: v.pinned, team_shared: !v.team_shared,
      });
      onChange && onChange();
      toast.success(v.team_shared ? "View unshared" : "Shared with your team");
    } catch {
      toast.error("Could not update sharing");
    }
  };

  if (own.length === 0 && shared.length === 0) {
    return (
      <div className="mt-6 rounded border border-ek-ink/10 p-3 text-xs text-ek-ink/55 leading-relaxed">
        <p className="font-semibold text-ek-ink mb-1 flex items-center gap-1.5"><Star size={12}/> Saved views</p>
        <p>None yet. Configure filters above and hit &quot;Save view&quot; to pin a personal preset here.</p>
      </div>
    );
  }
  return (
    <div className="mt-6 rounded border border-ek-ink/10 p-3 space-y-3" data-testid="saved-views-list">
      {own.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-ek-ink mb-2 flex items-center gap-1.5"><Star size={12}/> My views</p>
          <ul className="space-y-1">
            {own.map((v) => (
              <li key={v.view_id} className="flex items-center justify-between gap-1.5 group">
                <button
                  onClick={() => onApply(v)}
                  data-testid={`saved-view-${v.view_id}`}
                  className="text-left text-xs text-ek-ink hover:text-ek-purple truncate flex-1 min-w-0"
                  title={`${v.dimension} · ${v.pinned ? "pinned" : "saved"}`}
                >
                  {v.pinned && <Star size={10} className="inline text-amber-500 mr-1" fill="currentColor" />}
                  {v.team_shared && <Share2 size={10} className="inline text-ek-purple mr-1" />}
                  {v.name}
                </button>
                <button
                  onClick={() => toggleShare(v)}
                  data-testid={`saved-view-share-${v.view_id}`}
                  className={`flex-shrink-0 transition ${v.team_shared ? "text-ek-purple opacity-100" : "text-ek-ink/40 hover:text-ek-purple opacity-0 group-hover:opacity-100"}`}
                  title={v.team_shared ? "Stop sharing with your team" : "Share with your team (results still apply each user's own visibility)"}
                  aria-label={v.team_shared ? `Unshare ${v.name}` : `Share ${v.name}`}
                >
                  <Share2 size={12}/>
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete saved view "${v.name}"?`)) return;
                    await api.delete(`/explore/saved-views/${v.view_id}`);
                    onChange && onChange();
                    toast.success("View deleted");
                  }}
                  data-testid={`saved-view-delete-${v.view_id}`}
                  className="opacity-0 group-hover:opacity-100 text-ek-ink/40 hover:text-rose-600 flex-shrink-0"
                  aria-label={`Delete ${v.name}`}
                >
                  <Trash2 size={12}/>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {shared.length > 0 && (
        <div className="pt-3 border-t border-ek-ink/10">
          <p className="text-xs font-semibold text-ek-ink mb-2 flex items-center gap-1.5"><Share2 size={12}/> Team views</p>
          <ul className="space-y-1">
            {shared.map((v) => (
              <li key={v.view_id} className="group">
                <button
                  onClick={() => onApply(v)}
                  data-testid={`shared-view-${v.view_id}`}
                  className="w-full text-left text-xs text-ek-ink hover:text-ek-purple truncate"
                  title={`${v.dimension} · shared by ${v.shared_by}`}
                >
                  {v.name}
                  <span className="block text-[10px] text-ek-ink/50 mt-0.5">from {v.shared_by}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
